import * as React from "react";
import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/types/RootState";
import { MenuLevelConst, type MenuLevel } from "./menuLevel";
// Only `isOwnLevelOnlyPath` is USED here — the level derivation consults it.
// The other two are re-exported below without being imported.
import { isOwnLevelOnlyPath } from "@cleanup/utils/routesUtil/detailRoutes";

// The level vocabulary lives in its own module so the detail-route map can use
// it without importing this file, which imports the map. Re-exported because
// `useOrg()` hands out a `level` and its callers naturally reach for it here.
export { MenuLevelConst, type MenuLevel } from "./menuLevel";
// Which detail route a level opens, and which paths carry their level in the
// path itself. Lives in routesUtil — it is route knowledge, not context — and
// is re-exported because `getLevelFromSearch` below depends on it, so a reader
// of this file has to be able to follow it.
export {
  OWN_LEVEL_DETAIL_ROUTES,
  detailRouteFor,
  isOwnLevelOnlyPath,
} from "@cleanup/utils/routesUtil/detailRoutes";

export const OrgScopeConst = {
  /** No `?org=`: exactly one organisation is in scope — the login's own. */
  OWN: "own",
  /** `?org=all`: no single organisation; every org the login may see. */
  ALL: "all",
  /** `?org=<id>`: exactly one organisation, browsed from the cross level. */
  SLICED: "sliced",
} as const;

/**
 * How many organisations the current URL puts in scope, and which.
 *
 * This is what {@link OrgContextValue.orgId} alone cannot express: a null `orgId`
 * covers both `own` and `all`, which are opposite answers to "how many orgs am I
 * looking at?" (STIC-15615 / STIC-15790).
 */
export type OrgScope = (typeof OrgScopeConst)[keyof typeof OrgScopeConst];

/**
 * The `?org=` value meaning "every organisation I can see" — the cross level
 * with nothing sliced.
 *
 * The value the `scope` parameter takes. It was once the value of `org` too —
 * `?org=all` — and that spelling is still READ so existing links and bookmarks
 * keep working, but nothing writes it any more (STIC-15790):
 *
 * | URL | level | urlOrgId |
 * |---|---|---|
 * | `/trackers` | `own` | `null` |
 * | `/trackers?scope=all` | `cross` | `null` |
 * | `/trackers?org=42` | `cross` | `42` |
 *
 * Absence means **own level**, which is what makes it cheap to detect. The
 * all-orgs case had to become explicit for that to work: previously an absent
 * param meant all-orgs, and own level was flagged with a separate `level=own`
 * marker that every hand-rolled link had to remember to carry. That marker is
 * gone.
 *
 * A NORMAL login is the exception: it has exactly one organisation and no cross
 * level, so for it the parameter carries no information and is ignored outright
 * — `level` is always `own`, `orgId` always `null`, and the writers below are
 * no-ops. See {@link OrgProvider}.
 */
export const ORG_ALL = "all";
/** Names the sidebar level. `org` names the organisation; the two never mix. */
export const SCOPE_PARAM = "scope";

/**
 * Which sidebar level the current URL belongs to.
 *
 * Two parameters, one job each:
 *
 * | param | says |
 * |---|---|
 * | `scope=all` | the sidebar is at the ALL level |
 * | `org=<id>` | which organisation — a real id, never the word "all" |
 *
 * | URL | level |
 * |---|---|
 * | absent | `own` |
 * | `?org=<the login's own id>` | `own` |
 * | `?org=42` | `cross` |
 * | `?scope=all` | `cross`, whatever `org` says |
 *
 * **`scope` wins.** A detail page opened from ALL carries `?org=42&scope=all`: the page
 * gets the organisation it needs to fetch with, and the sidebar stays where the user left
 * it. One parameter could not say both, which is why `scope` exists — STIC-15790 recorded
 * "no `?scope=` param" as settled, on the grounds that own and all were already
 * distinguishable; that held until a detail page needed a real org id AND the ALL level at
 * the same time.
 *
 * `?org=all` is still read, so old links and bookmarks keep working. Nothing writes it.
 */
const getLevelFromSearch = (
  search: string,
  ownOrgId: number | null,
  pathname?: string
): MenuLevel => {
  const params = new URLSearchParams(search);
  if (params.get(SCOPE_PARAM) === ORG_ALL) return MenuLevelConst.CROSS;
  // The route carries the level for an own-exclusive path, so a foreign owner
  // in `?org=` does not read as "you are now browsing that organisation".
  if (pathname && isOwnLevelOnlyPath(pathname)) return MenuLevelConst.OWN;
  const org = params.get("org");
  if (org === null) return MenuLevelConst.OWN;
  // Legacy spelling of `scope=all`.
  if (org === ORG_ALL) return MenuLevelConst.CROSS;
  return ownOrgId !== null && Number(org) === ownOrgId
    ? MenuLevelConst.OWN
    : MenuLevelConst.CROSS;
};

/** True when the URL says the sidebar is at the ALL level, by either spelling. */
const isAllInSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.get(SCOPE_PARAM) === ORG_ALL || params.get("org") === ORG_ALL;
};

/**
 * Read the org id the url names, without React. `null` for absent and for the
 * legacy `org=all` spelling.
 */
const parseOrgParam = (search: string): number | null => {
  const org = new URLSearchParams(search).get("org");
  // The magic value is checked before parsing, so "all" never reaches parseInt.
  if (org === null || org === ORG_ALL) return null;
  // parseInt, not Number: `Number("")` is 0, so `isNaN(Number(org))` let `?org=`
  // through as NaN.
  const parsed = parseInt(org, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * `withOrg`, minus React — the single implementation both the hook and the
 * non-React route helpers use.
 *
 * It carries the **level**, and only the level. A caller that needs a particular
 * record's organisation names it itself (`?id=5&org=42`) and that is respected:
 * an `org` already in the url is never overwritten. At ALL it writes `scope=all`
 * and NO org, which is what makes a back-link correct — `withOrg("/users")` from
 * a detail opened at ALL returns to the list at ALL instead of dragging that one
 * record's org onto it. The forward link supplies the org; the level travels on
 * its own.
 *
 * `search` is the CURRENT url's query string, i.e. where the user is now.
 */
export const appendOrgLevel = (
  url: string,
  search: string,
  ownOrgId: number | null,
  isSingleOrgLogin: boolean,
  pathname?: string
): string => {
  // A single-org login has no level to state: both parameters are ignored for it
  // anyway (see isSingleOrgLogin), so writing them would be noise.
  if (isSingleOrgLogin) return url;

  const [path, query = ""] = url.split("?");
  const params = new URLSearchParams(query);

  if (isAllInSearch(search)) {
    if (!params.has(SCOPE_PARAM)) params.set(SCOPE_PARAM, ORG_ALL);
  } else {
    const atOwnLevel =
      getLevelFromSearch(search, ownOrgId, pathname) === MenuLevelConst.OWN;
    // On an own-exclusive route `?org=` names the record's OWNER, not the level
    // (that is the point of the route). Carrying it onto an outgoing link would
    // send the back-arrow to the CROSS list sliced to an organisation the user
    // may not even see — so here, and only here, the level's own org wins.
    const scopeOrgId =
      pathname && isOwnLevelOnlyPath(pathname)
        ? ownOrgId
        : parseOrgParam(search) ?? (atOwnLevel ? ownOrgId : null);
    if (scopeOrgId != null && !params.has("org")) {
      params.set("org", String(scopeOrgId));
    }
  }

  const resulting = params.toString();
  return resulting ? `${path}?${resulting}` : path;
};

/**
 * The entity types the backend actually reads `onlyOwnedByOrg` for, by the
 * `queryObjectType` every advanced-search payload already carries:
 * `QueryObjectType.DEVICE_USAGE_PROFILE` (:1443) and `IMAGE_LIBRARY` (:1911),
 * `RoleService:622`, `DashboardServiceImpl:345`. Anywhere else the flag is inert
 * payload today and a silent behaviour change the day someone implements it.
 *
 * **This list is meant to move.** Implement the flag for another entity and add
 * its `queryObjectType` here in the same commit — that one line is the diff
 * reviewers need in order to see the behaviour change. The day every searchable
 * entity honours it, delete the set and the `has()` call with it: the rule
 * collapses to `if (onlyOwnedByOrg) target.onlyOwnedByOrg = true`, and nothing is lost,
 * because by then there is nothing left to distinguish.
 */
const INHERITS_FROM_ABOVE = new Set([
  "ROLE_MANAGER",
  "BI",
  "DEVICE_USAGE_PROFILE",
  "IMAGE_LIBRARY",
  // Both are GeoLayer rows and share one predicate
  // (SharedGeoLayerQueryUtil.applyManagementScope), which now reads the flag
  // instead of branching on the login's organisation type.
  "GEO_LAYER",
  "SITE_MAP",
  // STIC-16328: a geozone is shared down too (visible for child orgs, or
  // explicitly accessible by an org), so a slice must drop the partner's zones
  // and the tags that only reach the org through them.
  "AREA",
  "GEOZONE_TAG",
]);

/**
 * The documented exceptions to "own level shows only what this organisation
 * owns". One today: device returns (STIC-15976) — a return is raised either by
 * the partner or by its customer directly with Sensolus, so a partner at own
 * level must see its whole tree. Adding to this set means writing the argument
 * here, where the rule is, rather than in a page.
 */
const PARTNER_TREE_AT_OWN_LEVEL = new Set(["RMA_DECLARATION"]);

interface OrgContextValue {
  /**
   * The organisation id **the URL literally names**, or `null` when it names
   * none. Nothing is resolved: it is `null` at own level with no parameter, and
   * `null` at `?scope=all`.
   *
   * **Almost nothing should read this.** It exists for the menu machinery,
   * which has to know what the URL says rather than what it means — and for a
   * pre-login screen like `LoginMobile`, which brands itself from the org in the
   * link, not from an org it does not have yet.
   *
   * If you are scoping a request, gating an action, or building a link, you want
   * {@link OrgContextValue.scopeOrgId}, {@link OrgContextValue.isAllOrgs} or
   * {@link OrgContextValue.withOrg}. It used to be called `orgId`, and that name
   * is what made `if (orgId)` read as "an organisation is selected" — false at
   * own level and at `all` alike, which is the whole STIC-15790 bug class.
   */
  urlOrgId: number | null;
  /** How many organisations the URL puts in scope. See {@link OrgScope}. */
  orgScope: OrgScope;
  /**
   * The single organisation a list request should be scoped to, or `null` when
   * more than one is in view. Send it as `selectedOrgId`.
   *
   * | orgScope | value |
   * |---|---|
   * | `own` | the login's own organisation |
   * | `sliced` | that organisation |
   * | `all` | `null` |
   *
   * The point of it: `orgId` is `null` at BOTH `own` and `all`, so pages that
   * guarded with `if (orgId)` sent no scope at own level and the backend fell
   * back to "everything this login may see" — every organisation, for a SYSTEM
   * login (STIC-15790).
   */
  scopeOrgId: number | null;
  /**
   * Send `onlyOwnedByOrg: true` so a list shows only what {@link
   * OrgContextValue.scopeOrgId} OWNS, never rows inherited from an org above it.
   *
   * True at CROSS level — `?org=<id>` and `?org=all` alike. Only the entity types
   * in {@link INHERITS_FROM_ABOVE} honour the flag server-side; for everything
   * else it is inert, because those entities inherit nothing.
   *
   * Own level deliberately does NOT slice, and this is the contract STIC-15615
   * wrote rather than debt left behind — all three backend call sites name the
   * level in so many words (`QueryObjectType.java:1433`, `:1915`,
   * `DashboardServiceImpl.java:343`: "cross-level org browsing slices strictly
   * on ownership"). The two situations differ:
   *
   * - SLICED — an org above is inspecting org 42 from outside. Rows it shared
   *   down to org 42 are rows it already manages at its own level, so repeating
   *   them here is noise. Show what org 42 itself authored.
   * - ALL — the same question asked of every child at once: "owned entities of
   *   child orgs and itself". Shared rows are own level's business, not this
   *   view's, so they are excluded here too.
   * - OWN — the organisation looking at itself: "owned AND shared entities". A
   *   role System published to this partner is part of the partner's working
   *   set; dropping it would hide something the partner actually uses.
   *
   * In practice this only ever changes a PARTNER login: for SYSTEM
   * `addCriteriaJunction` collapses to "own only" either way (nothing sits
   * above it), and a NORMAL login can never reach the sliced state at all.
   *
   * To widen this to own level later, it is this one line — not a migration.
   */
  onlyOwnedByOrg: boolean;
  /**
   * The page is showing MORE THAN ONE organisation: the ALL scope (`?scope=all`).
   * Pages use it to disable actions that need a single owning organisation, and
   * to early-return `<NotAvailableAtThisLevel />`.
   *
   * It was called `isCrossOrg`, which read as `level === CROSS` — and cross
   * covers `sliced` too, where exactly one organisation IS in scope. The two
   * vocabularies are deliberately different sizes and must not be confused:
   *
   * | question | type | values |
   * |---|---|---|
   * | which menu renders | `MenuLevel` | `own` · `cross` |
   * | how many orgs are in scope | `OrgScope` | `own` · `sliced` · `all` |
   *
   * `isAllOrgs` is the second one. Not the same as `!scopeOrgId` either: that is
   * also true at own level, where one organisation is in scope and the action
   * should stay enabled.
   */
  isAllOrgs: boolean;
  /**
   * The login belongs to a NORMAL organisation: one org, no cross level, and
   * `?org=` ignored outright. See {@link OrgProvider}.
   */
  isNormalUser: boolean;
  /**
   * A PARTNER login at `?scope=all` — showing everything the partner manages
   * rather than one organisation, so the page links to partner routes and hides
   * actions that need a single owning org. Replaces the `_partner` route
   * variants (STIC-15365).
   *
   * A partner that slices one of its customers is doing ordinary org-scoped
   * browsing and must NOT get this treatment, which is why it is derived from
   * {@link OrgContextValue.isAllOrgs} rather than from the login alone.
   */
  isPartner: boolean;
  /**
   * Name the organisation the url points at, or pass `null` to drop the `org`
   * param. It never touches `scope`, so it changes *which* organisation, never
   * *which level* — `updateOrg(null)` at `scope=all` stays at All, and at own
   * level stays at own. Changing the level is the selector's job.
   */
  updateOrg: (newOrgId: number | null) => void;
  /**
   * The sidebar level the current URL belongs to: `own` when there is no `?org=`
   * at all, `cross` otherwise. Read by SidebarBody to break the tie when both
   * menus register the same path.
   */
  level: MenuLevel;
  /**
   * Append the current org scope to a link so navigating to it stays where the
   * user is. Picks `?`/`&` automatically, and leaves a url that already carries
   * an `org=` untouched.
   *
   * **It names the level in all three states** — own included:
   *
   * | url now | result |
   * |---|---|
   * | own level | `?org=<login org id>` |
   * | All | `?scope=all`, and no org — see below |
   * | `?org=42` | `?org=42` |
   *
   * Own level used to be left bare, on the grounds that the absence of the
   * parameter IS the level. That is true for the sidebar, which derives
   * {@link OrgContextValue.level} from exactly that. It is not true for the page
   * on the other end of the link: one that only knows what the url tells it
   * arrived with no organisation at all, which is how a detail page opened from
   * own level ended up unscoped (STIC-15790).
   *
   * At All it appends **no org** on purpose: carrying one that means "every
   * organisation" into a detail page would hand it that as the scope of a single
   * record. The record's organisation is the caller's job — this helper never
   * overwrites an `org=` the url already carries — and the level travels beside
   * it, which is what lets a back-link return to the list at All. A NORMAL login
   * likewise gets nothing appended: both parameters are ignored for it (see
   * {@link OrgProvider}).
   *
   * Building the parameter by hand is still wrong, and for the same reason as
   * before: `org=${orgId}` yields the string `org=null` at own level AND at
   * all-orgs. See MENU_RESOLUTION.md.
   */
  withOrg: (url: string) => string;
  /**
   * Put the org contract on a search payload. **This is the only place the
   * contract is written**; a page that hand-rolls it will drift the day a field
   * is renamed or the rule changes, which is exactly how `selectedOrgId` came to
   * be missing at own level on some pages and `onlyOwnedByOrg` to be sent by pages
   * whose entity does not read it.
   *
   * ```ts
   * const { applyOrgScope } = useOrg();
   * const onRequestData = (params) => client.search(applyOrgScope(params));
   * ```
   *
   * | level | what it writes |
   * |---|---|
   * | own | `selectedOrgId` = the login's own org |
   * | sliced | `selectedOrgId` = that org, plus `onlyOwnedByOrg: true` when `inherits` |
   * | All | `onlyOwnedByOrg: true` when `inherits`, and no `selectedOrgId` |
   *
   * It takes no options on purpose. Whether `onlyOwnedByOrg` belongs on the payload
   * is a property of the ENTITY, not of the page, and the payload already says
   * which entity it is — `queryObjectType`. Same for the one documented
   * exception (STIC-15976, device returns). Both live in the two sets above, so
   * a page cannot get them wrong and a reader has one place to look.
   */
  applyOrgScope: <T extends object>(params: T) => T;
}

const OrgContext = React.createContext<OrgContextValue | null>(null);

interface OrgProviderProps {
  children: React.ReactNode;
}

export const OrgProvider = ({ children }: OrgProviderProps) => {
  const location = useLocation();
  const history = useHistory();
  const loginInfo = useSelector((state: RootState) => state.user);
  const ownOrgId = loginInfo?.organisation?.id ?? null;

  /**
   * A NORMAL login has exactly one organisation and no cross level, so `?org=`
   * carries no information for it — and it cannot be trusted either: 43 detail
   * pages call {@link OrgContextValue.updateOrg} with the owner of the record
   * they just loaded, and 221 link sites still append `org=${orgId}` by hand.
   * Either one used to push such a login onto a cross level it does not have,
   * which hid every own-level menu item and the Home/Admin footer, and bounced
   * the user to `/organization?org=<id>` from the back arrow (STIC-15790
   * follow-up). The parameter is therefore ignored outright for these logins:
   * `level` is always `own`, `orgId` always `null`, the writers are no-ops.
   *
   * Before the user info has loaded the type is unknown and the URL decides, as
   * it does for every other login.
   */
  const isSingleOrgLogin = loginInfo?.organisation?.organisationType === "NORMAL";

  /**
   * The `org` param verbatim: `null` (own level), `"all"`, an id, or junk.
   *
   * Junk is real: 221 link sites build `org=${orgId}` by hand, which yields the
   * string `"null"` whenever no org is sliced. Anything unparseable is treated as
   * `all` rather than as absent, because that is what those links produced before
   * this rework and the cross level has no other safety net. Own-level drill-ins
   * are covered instead by SidebarBody's rule 2.
   */
  const getRawOrgParam = useCallback(
    (loc: typeof location) => new URLSearchParams(loc.search).get("org"),
    []
  );

  const getOrgIdFromLocation = useCallback(
    (loc: typeof location) => {
      const org = getRawOrgParam(loc);
      // The magic value is checked before parsing, so "all" never reaches parseInt.
      if (org === null || org === ORG_ALL) return null;
      // parseInt, not Number: `Number("")` is 0, so the previous
      // `isNaN(Number(org)) ? null : parseInt(org)` let `?org=` through as NaN.
      const parsed = parseInt(org, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    [getRawOrgParam]
  );

  const updateOrg = (newOrgId: number | null) => {
    // A single-org login has nothing to slice — see isSingleOrgLogin.
    if (isSingleOrgLogin) return;
    const param = new URLSearchParams(location.search);
    const atAll = isAllInSearch(location.search);
    // Already at own level and asked for own level: nothing to write.
    if (!param.has("org") && !atAll && (!newOrgId || newOrgId === ownOrgId)) {
      return;
    }
    // **It corrects the organisation, never the level.**
    //
    // Own level lists what the organisation owns PLUS what an org above shared
    // down to it — a role System published, a dashboard, a geolayer, an image.
    // Those rows are owned by a DIFFERENT organisation, so a detail page loading
    // one and reporting its owner would write a foreign org into the url. At own
    // level that is not a correction: `getLevelFromSearch` reads a foreign org as
    // CROSS, so the sidebar flips to the cross menu, `onlyOwnedByOrg` turns on, and
    // AppLayout remounts — the "opening a detail moves the selector" bug this
    // whole ticket exists to remove (STIC-15790 bug #2), arriving from the other
    // side.
    //
    // So at own level a foreign owner is simply not written. The page still
    // renders: it loads by `id`, not by org. Three detail pages had grown their
    // own version of this guard (RoleManagerUpdate, DeviceUsageProfileManage) and
    // two had none (BiDashBoardManageContent, GeolayerUpdate) — which is what a
    // rule living in the pages rather than here always ends up looking like.
    //
    // An own-EXCLUSIVE route is the exception, and the reason it exists: there
    // the path already pins the level, so writing the record's owner cannot move
    // the sidebar and the guard would only stop the url from naming the owner.
    const atOwnLevel =
      getLevelFromSearch(location.search, ownOrgId) === MenuLevelConst.OWN;
    if (
      atOwnLevel &&
      !isOwnLevelOnlyPath(location.pathname) &&
      newOrgId != null &&
      newOrgId !== ownOrgId
    ) {
      return;
    }
    const before = param.toString();
    // **`scope` is never touched here.** Every caller of this is a detail page
    // saying "the record I loaded belongs to org N" — it corrects which
    // organisation the url names, not which level the sidebar is at. Changing
    // the level is the selector's job, and it writes both parameters itself.
    // Dropping `scope=all` here is precisely the "opening a detail moves the
    // selector" bug (STIC-15790 bug #2), and it is why `&ownerOrg=` had to exist
    // before `scope` did.
    if (newOrgId) {
      param.set("org", String(newOrgId));
    } else {
      param.delete("org");
    }
    if (param.toString() !== before) {
      history.replace({ search: param.toString() });
    }
  };

  const orgId = isSingleOrgLogin ? null : getOrgIdFromLocation(location);
  const level = isSingleOrgLogin
    ? MenuLevelConst.OWN
    : getLevelFromSearch(location.search, ownOrgId, location.pathname);

  // ---- Org scope, derived from `level` + `orgId` together ----
  // Neither alone is enough: `orgId` is null at own level AND at `?org=all`,
  // and `level` cannot tell `all` from a sliced org. See OrgScope.
  const atOwnLevel = level === MenuLevelConst.OWN;
  // `scope=all` wins over `org`: a detail opened from ALL carries both, and the level is
  // what the sidebar and `onlyOwnedByOrg` follow.
  const orgScope: OrgScope = isSingleOrgLogin
    ? OrgScopeConst.OWN
    : isAllInSearch(location.search)
      ? OrgScopeConst.ALL
      : atOwnLevel
        ? OrgScopeConst.OWN
        : OrgScopeConst.SLICED;
  // Own level has no `?org=` but is still exactly one organisation: the login's.
  // The organisation named by the url, else the login's own at own level, else none.
  // At ALL a list carries no `org` and gets null; a detail carries one and gets it.
  const scopeOrgId = orgId ?? (atOwnLevel ? ownOrgId : null);
  const isAllOrgs = orgScope === OrgScopeConst.ALL;
  // Cross level — SLICED and ALL alike; see the field's doc. `isSingleOrgLogin`
  // is redundant here by construction (a NORMAL login is pinned to OWN above, so
  // it can never leave it); kept as an explicit assertion of the rule.
  const onlyOwnedByOrg = !isSingleOrgLogin && orgScope !== OrgScopeConst.OWN;
  const isPartner =
    loginInfo?.organisation?.organisationType === "PARTNER" && isAllOrgs;

  // Derive scope mode synchronously from the URL. Keys off the *parsed* org id,
  // not the param's presence: `?org=all` is present but is not a single-org scope.
  // (No consumers outside this file today — kept for the dashboard landing paths.)
  const withOrg = useCallback(
    (url: string) =>
      appendOrgLevel(
        url,
        location.search,
        ownOrgId,
        isSingleOrgLogin,
        location.pathname
      ),
    [location.search, location.pathname, ownOrgId, isSingleOrgLogin]
  );


  const isPartnerLogin = loginInfo?.organisation?.organisationType === "PARTNER";
  const applyOrgScope = useCallback(
    <T extends object>(params: T): T => {
      const target = params as T & {
        queryObjectType?: string;
        selectedOrgId?: number;
        onlyOwnedByOrg?: boolean;
      };
      const entity = target.queryObjectType ?? "";
      const partnerTree =
        PARTNER_TREE_AT_OWN_LEVEL.has(entity) &&
        isPartnerLogin &&
        orgScope === OrgScopeConst.OWN;
      if (scopeOrgId != null && !partnerTree) {
        target.selectedOrgId = scopeOrgId;
      }
      if (onlyOwnedByOrg && INHERITS_FROM_ABOVE.has(entity)) {
        target.onlyOwnedByOrg = true;
      }
      return params;
    },
    [scopeOrgId, onlyOwnedByOrg, orgScope, isPartnerLogin]
  );

  return (
    <OrgContext.Provider
      value={{
        urlOrgId: orgId,
        orgScope,
        scopeOrgId,
        onlyOwnedByOrg,
        isAllOrgs,
        isNormalUser: isSingleOrgLogin,
        isPartner,
        updateOrg,
        level,
        withOrg,
        applyOrgScope,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextValue => {
  const context = React.useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
};

/**
 * The organisation the report pages name in their requests (STIC-15980): the one
 * selected in the header, none at own level, where the backend takes the login's own.
 */
export const useReportOrgId = (): number | undefined => {
  const { orgScope, scopeOrgId } = useOrg();
  return orgScope === OrgScopeConst.SLICED ? scopeOrgId ?? undefined : undefined;
};
